/**
 * Turn a graphql-request failure into something safe to render.
 *
 * graphql-request puts the *entire* response body into `Error.message` — for a
 * gateway 5xx that body is a full HTML error page, which (when rendered straight
 * into a dialog) blows the layout apart and pushes the buttons off-screen. So:
 * prefer the real GraphQL error, fall back to a clean status message, and hard-cap
 * anything else.
 */
const MAX_LEN = 160

interface GqlErrorish {
  message?: unknown
  response?: {
    status?: number
    errors?: Array<{ message?: string }>
  }
}

export function friendlyGqlError(
  err: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  if (!err) return fallback
  const e = err as GqlErrorish

  // A real GraphQL error (e.g. the BE's push-completeness rejection) — use it.
  const gqlMessage = e.response?.errors?.[0]?.message
  if (gqlMessage) return truncate(gqlMessage)

  // Transport failure — never surface the body (it may be an HTML page).
  const status = e.response?.status
  if (status && status >= 500) {
    return `The server is unavailable right now (${status}). Please try again.`
  }
  if (status) return `Request failed (${status}).`

  const message = typeof e.message === 'string' ? e.message : ''
  return message ? truncate(message) : fallback
}

const truncate = (s: string) => (s.length > MAX_LEN ? `${s.slice(0, MAX_LEN)}…` : s)
